import { Routes } from '@angular/router';
import { ADMIN_ROUTES } from './admin/admin.routes';
import { PlaceholderPageComponent } from './features/placeholder/placeholder-page.component';
import { authGuard } from './core/guards/auth.guard';
import { RETIRED_GALLERY_PATHS, toPaperDocs } from './core/guards/paper-docs-redirect';

/**
 * Application routing.
 *
 * The migrated admin pages now mount at the SETTLED GROUPED rail URLs
 * (`/estate/*`, `/workplace/*`, `/servicing/*`, `/people/*`, `/governance/*`,
 * `/communications/*`, `/platform/*`), hosted inside the ws-root Paper shell.
 * `ADMIN_ROUTES` is a single componentless providers-layout route (`path: ''`)
 * whose children are those grouped pages — they render straight into ws-root's
 * `<router-outlet>` (no second shell). The app boots to `/overview`.
 *
 * The self-contained sibling surfaces copied alongside admin (signage kiosk,
 * games centre, content studio, live events) keep their own canonical paths and
 * render full-bleed (ws chrome suppressed — see app.ts `chromeless`). They are
 * declared BEFORE the admin layout so their prefixes win over the layout's
 * catch-all.
 */
export const routes: Routes = [
  // Boot into the migrated admin surface, now at its settled grouped home.
  { path: '', pathMatch: 'full', redirectTo: 'overview' },

  // Landing for a signed-in, non-admin user. Declared BEFORE the admin layout
  // and with NO guard — adminGuard denies non-admins here, so it must sit
  // outside the guarded layout or it would loop the denial (bd 7p4).
  {
    path: 'no-access',
    loadComponent: () =>
      import('./features/auth/no-access/no-access.component').then(
        (m) => m.NoAccessComponent,
      ),
    title: 'No access — RICOH Workplace',
  },

  // The same terminal surface when the admin-access check could not reach the
  // server (P4-70): a failure is no answer about the account. Ungated, beside
  // /no-access, for the same reason.
  {
    path: 'unreachable',
    loadComponent: () =>
      import('./features/auth/no-access/no-access.component').then(
        (m) => m.NoAccessComponent,
      ),
    data: { reason: 'unreachable' },
    title: 'Can’t reach the server — RICOH Workplace',
  },

  // The end of the sign-in loop. `authGuard` redirects here (auth.guard.ts) and
  // `SessionService` hard-navigates here (session.service.ts) when the app has
  // been to login-web and returned still unable to see a session. Declared
  // BEFORE the admin layout and with NO guard — it must render without a session
  // (it explains why there isn't one) or the guard would loop it back to itself.
  {
    path: 'sign-in-problem',
    loadComponent: () =>
      import('./features/auth/sign-in-problem/sign-in-problem.component').then(
        (m) => m.SignInProblemComponent,
      ),
    title: 'Sign-in problem — RICOH Workplace',
  },

  // Where the API's Microsoft 365 OAuth callback sends the consent popup
  // (`ConnectController.handleCallback` redirects to
  // `/#/settings/integrations[/<provider>]`, which main.ts rewrites to this
  // path form). Nothing else lives under `/settings`; this page hands the
  // result to the connect modal and closes, or forwards to
  // `/platform/integrations/<provider>`. No guard: the hand-off needs no
  // session, and the forward lands on guarded routes.
  {
    path: 'settings/integrations',
    loadComponent: () =>
      import('./features/settings/pages/integrations/oauth-return.component').then(
        (m) => m.OAuthReturnComponent,
      ),
    title: 'Connecting Microsoft 365 — RICOH Workplace',
  },
  {
    path: 'settings/integrations/:provider',
    loadComponent: () =>
      import('./features/settings/pages/integrations/oauth-return.component').then(
        (m) => m.OAuthReturnComponent,
      ),
    title: 'Connecting Microsoft 365 — RICOH Workplace',
  },

  // The old design-system gallery's URL (/platform/component-gallery). The
  // gallery was removed on 8 Oct 2026; Ricoh Paper's own docs site replaces
  // it, so the old link leaves for that site. No guard: the site is public.
  // Declared here, ahead of the admin layout, so its sign-in and admin guards
  // never answer first. `core/guards/paper-docs-redirect.ts`.
  ...RETIRED_GALLERY_PATHS.map((path) => ({
    path,
    pathMatch: 'full' as const,
    canActivate: [toPaperDocs],
    children: [],
  })),

  // ── Self-contained sibling surfaces (own shells / public), copied with admin. ──
  {
    path: 'signage',
    loadChildren: () =>
      import('./features/signage/signage.routes').then((m) => m.SIGNAGE_ROUTES),
  },
  {
    path: 'games',
    loadChildren: () => import('./features/games/games.routes').then((m) => m.GAMES_ROUTES),
  },
  {
    path: 'preview/games',
    loadChildren: () =>
      import('./features/games/games.routes').then((m) => m.GAMES_PREVIEW_ROUTES),
  },
  {
    path: 'content',
    loadChildren: () =>
      import('./features/content/content.routes').then((m) => m.CONTENT_ROUTES),
  },
  {
    // The visitor's own page — reached from the link in their invitation. PUBLIC:
    // no guard, no admin shell (`v` is chromeless in app.ts), no viewer load and
    // no analytics on this path. The key travels in the URL fragment and is taken
    // out of the address bar before bootstrap (main.ts, visitor-page-link.ts).
    path: 'v/:customerId',
    loadComponent: () =>
      import('./features/visitors/visitor-form/visitor-page.component').then(
        (m) => m.VisitorPageComponent,
      ),
    title: 'Your visit',
  },
  {
    // The visitor admin screens on sample data — no session, no API.
    path: 'preview/visitors',
    loadChildren: () =>
      import('./features/visitors/preview/visitors-preview.routes').then(
        (m) => m.VISITORS_PREVIEW_ROUTES,
      ),
  },
  {
    // The Game Centre settings page on sample data: no session, no API.
    // (`/preview/games` is the employee-facing arcade; this is the admin page.)
    path: 'preview/game-centre',
    loadChildren: () =>
      import('./features/settings/pages/games/games-settings-preview.routes').then(
        (m) => m.GAMES_SETTINGS_PREVIEW_ROUTES,
      ),
  },
  {
    path: 'preview/content',
    loadChildren: () =>
      import('./features/content/content.routes').then((m) => m.CONTENT_PREVIEW_ROUTES),
  },
  {
    // The policy list with tabs on sample data — no session, no API.
    path: 'preview/policies',
    loadChildren: () =>
      import('./features/settings/pages/policies/list/policies-list-preview.routes').then(
        (m) => m.POLICIES_LIST_PREVIEW_ROUTES,
      ),
  },
  {
    path: 'live-events',
    loadChildren: () =>
      import('./features/live-events/live-events.routes').then((m) => m.LIVE_EVENTS_ROUTES),
  },
  {
    path: 'catering/order',
    canActivate: [authGuard],
    loadComponent: () =>
      import(
        './features/catering/pages/orderless-order/orderless-catering-page.component'
      ).then((m) => m.OrderlessCateringPageComponent),
    title: 'Order catering — RICOH Workplace',
  },

  // ── The operator console — Ricoh staff working ACROSS customers. ──
  // Beside the admin layout, not inside it: its adminGuard asks for an admin
  // action in the ACTIVE customer, which a Customer Support person does not
  // hold. Each operator area carries the guard its API demands.
  {
    path: 'operator',
    canActivate: [authGuard],
    loadChildren: () => import('./operator/operator.routes').then((m) => m.OPERATOR_ROUTES),
  },

  // ── The migrated admin, grouped, hosted inside the ws-root shell. ──
  // A componentless providers layout (path: '') whose children are the grouped
  // pages. Its own `**` child redirects unknown paths to Overview, so this is
  // effectively the app catch-all; the bare PlaceholderPageComponent below is a
  // defensive last resort only.
  ...ADMIN_ROUTES,

  { path: '**', component: PlaceholderPageComponent },
];
